import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { LogOut, FileText, Menu, Building2, Users, MapPin, Sparkles, Mail, CalendarCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';

// 個別タブコンポーネントをインポート
import NewsManagement from '../components/admin/NewsManagement';
import NavigationManagement from '../components/admin/NavigationManagement';
import CompanyInfoManagement from '../components/admin/CompanyInfoManagement';
import RecruitmentManagement from '../components/admin/RecruitmentManagement';
import OfficeManagement from '../components/admin/OfficeManagement';
import ApplicationManagement from '../components/admin/ApplicationManagement';
import UserManagement from '../components/admin/UserManagement';
import FeatureManagement from '../components/admin/FeatureManagement';
import EmailManagement from '../components/admin/EmailManagement';
import TourReservationManagement from '../components/admin/TourReservationManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('news'); // アクティブなタブを管理
  const [userRole, setUserRole] = useState(null); // ユーザーの権限（nullは未取得）
  const [companyInfo, setCompanyInfo] = useState(null);
  const [stats, setStats] = useState({
    newsCount: 0,
    navigationCount: 0,
    recruitmentCount: 0,
    officesCount: 0,
    applicationsCount: 0,
    usersCount: 0,
    featuresCount: 0,
    unreadCount: 0,
    tourCount: 0,
  });

  useEffect(() => {
    // ローカルストレージからユーザー情報を取得
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('User role:', user.role); // デバッグ用
        setUserRole(user.role || 'editor');
      } catch (error) {
        console.error('ユーザー情報の取得エラー:', error);
        setUserRole('editor'); // デフォルトは編集者
      }
    } else {
      setUserRole('editor'); // デフォルトは編集者
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    loadStats();
    api.get('/company-info').then((r) => setCompanyInfo(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadStats = async () => {
    try {
      const [newsRes, navRes, recRes, officeRes, appRes, featuresRes] = await Promise.all([
        api.get('/news'),
        api.get('/navigation'),
        api.get('/recruitment'),
        api.get('/offices'),
        api.get('/applications'),
        api.get('/features'),
      ]);

      let tourCount = 0;
      try {
        const tourRes = await api.get('/tour-reservations');
        tourCount = tourRes.data.length;
      } catch (_) {
        // ignore
      }

      // ユーザー一覧は admin のみアクセス可能
      let usersCount = 0;
      try {
        const usersRes = await api.get('/users');
        usersCount = usersRes.data.length;
      } catch (_) {
        // 403 等は無視
      }

      setStats({
        newsCount: newsRes.data.length,
        navigationCount: navRes.data.length,
        recruitmentCount: recRes.data.length,
        officesCount: officeRes.data.length,
        applicationsCount: appRes.data.length,
        featuresCount: featuresRes.data.length,
        unreadCount: appRes.data.filter((a) => !a.is_read).length,
        usersCount,
        tourCount,
      });
    } catch (error) {
      console.error('統計データの取得に失敗しました:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast({
      title: 'ログアウトしました',
    });
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* ヘッダー */}
      <header className="bg-gradient-to-r from-white to-gray-50 border-b border-amber-500/30 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <img 
                src={companyInfo?.logo_url || "https://customer-assets.emergentagent.com/job_presage-proposal/artifacts/gcigf741_Presage-Corporation-150x150.png"} 
                alt="Presage Corporation" 
                className="h-8 sm:h-10 w-auto flex-shrink-0"
                data-testid="admin-logo-img"
              />
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <span className="hidden sm:inline text-lg font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent truncate">
                  Presage Corporation
                </span>
                <div className="hidden sm:block h-8 w-px bg-amber-500/30"></div>
                <h1 className="text-base sm:text-xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent whitespace-nowrap">管理画面</h1>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              size="sm"
              className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 hover:text-amber-500 transition-all flex-shrink-0"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">ログアウト</span>
            </Button>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* 統計カード */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4 md:gap-5 mb-6 sm:mb-8">
          {[
            { title: 'ニュース', value: stats.newsCount, icon: FileText, tab: 'news' },
            { title: 'メニュー', value: stats.navigationCount, icon: Menu, tab: 'navigation' },
            { title: 'メール', value: stats.unreadCount, icon: Mail, tab: 'email' },
            ...(userRole === 'admin' ? [{ title: 'ユーザー', value: stats.usersCount, icon: Users, tab: 'users' }] : []),
            { title: '会社概要', value: '1', icon: Building2, tab: 'company' },
            { title: '採用情報・応募', value: stats.recruitmentCount, icon: Users, tab: 'recruitment' },
            { title: '事業内容', value: stats.officesCount, icon: MapPin, tab: 'offices' },
            { title: '見学予約', value: stats.tourCount, icon: CalendarCheck, tab: 'tour' },
            { title: '私たちの強み', value: stats.featuresCount, icon: Sparkles, tab: 'features' },
          ].map((stat, index) => (
            <div
              key={index}
              onClick={() => setActiveTab(stat.tab)}
              className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/30 backdrop-blur-sm rounded-lg p-3 sm:p-4 hover:border-amber-500/50 transition-all cursor-pointer hover:scale-105 transform"
              data-testid={`stats-card-${stat.tab}`}
            >
              <div className="flex items-center justify-between gap-1.5">
                <div className="min-w-0">
                  <p className="text-xs sm:text-[13px] text-gray-600 leading-tight">{stat.title}</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent mt-1">
                    {stat.value}
                  </p>
                </div>
                <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>

        {/* タブナビゲーション */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="w-full overflow-x-auto">
            <TabsList className={`grid ${userRole === 'admin' ? 'grid-cols-9' : 'grid-cols-8'} w-full min-w-[800px] md:min-w-0 bg-white/50 border border-amber-500/30 p-1 h-auto`}>
              <TabsTrigger value="news" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <FileText className="w-4 h-4 mr-1 hidden lg:inline" />
                ニュース
              </TabsTrigger>
              <TabsTrigger value="navigation" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <Menu className="w-4 h-4 mr-1 hidden lg:inline" />
                メニュー
              </TabsTrigger>
              <TabsTrigger value="email" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <Mail className="w-4 h-4 mr-1 hidden lg:inline" />
                メール
              </TabsTrigger>
              {userRole === 'admin' && (
                <TabsTrigger value="users" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                  <Users className="w-4 h-4 mr-1 hidden lg:inline" />
                  ユーザー
                </TabsTrigger>
              )}
              <TabsTrigger value="company" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <Building2 className="w-4 h-4 mr-1 hidden lg:inline" />
                会社概要
              </TabsTrigger>
              <TabsTrigger value="recruitment" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <Users className="w-4 h-4 mr-1 hidden lg:inline" />
                採用情報
              </TabsTrigger>
              <TabsTrigger value="offices" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <MapPin className="w-4 h-4 mr-1 hidden lg:inline" />
                事業内容
              </TabsTrigger>
              <TabsTrigger value="tour" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <CalendarCheck className="w-4 h-4 mr-1 hidden lg:inline" />
                見学予約
              </TabsTrigger>
              <TabsTrigger value="features" className="w-full whitespace-nowrap data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm px-1 py-2">
                <Sparkles className="w-4 h-4 mr-1 hidden lg:inline" />
                私たちの強み
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="news" className="mt-6">
            <NewsManagement onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="navigation" className="mt-6">
            <NavigationManagement onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="email" className="mt-6">
            <EmailManagement onUpdate={loadStats} />
          </TabsContent>

          {userRole === 'admin' && (
            <TabsContent value="users" className="mt-6">
              <UserManagement />
            </TabsContent>
          )}

          <TabsContent value="company" className="mt-6">
            <CompanyInfoManagement />
          </TabsContent>

          <TabsContent value="recruitment" className="mt-6">
            <div className="space-y-10">
              <RecruitmentManagement onUpdate={loadStats} />
              <div className="border-t border-amber-500/20 pt-8">
                <ApplicationManagement onUpdate={loadStats} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="offices" className="mt-6">
            <OfficeManagement onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="tour" className="mt-6">
            <TourReservationManagement onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="features" className="mt-6">
            <FeatureManagement />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
